import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useMemo,
  useCallback,
  FC,
  useRef,
  useEffect,
} from 'react';
import { get, set, has, unset, merge, cloneDeep } from 'lodash';

import { ContextStore, ContextValue, Preset } from '../types';

import { ImpactAiTracker } from './useImpactAiTracking';

export interface ContextAPI {
  getValue: <T extends ContextValue>(path: string, defaultValue?: T) => T;
  setValue: (path: string, value: ContextValue) => void;
  hasValue: (path: string) => boolean;
  deleteValue: (path: string) => void;
  updateValues: (updates: Record<string, ContextValue>) => void;
  mergeContext: (newContext: Record<string, ContextValue>) => void;
  parseTemplate: (template: string, defaultValue?: string) => string;
  resetContext: () => void;
  getFullContext: () => Record<string, ContextValue>;
  toFlatContext: () => Record<string, string>;
  subscribe: (callback: (context: ContextStore) => void) => () => void;
}

const Context = createContext<ContextAPI | undefined>(undefined);

interface ContextProviderProps {
  children: ReactNode;
  initialContext?: ContextStore;
  preset?: Preset | null;
  tracker?: ImpactAiTracker;
}

/**
 * Parse a template string with {{variable}} syntax and replace with values from context
 * Supports nested paths with dot notation: {{user.name}}
 */
const parseTemplateString = (template: string, context: ContextStore, defaultValue = ''): string =>
  // Match {{variable}} or {{variable,placeholder}}
  template.replace(/\{\{([^}]+)\}\}/g, (_, match: string) => {
    const [path, ...placeholderParts] = match.split(',');
    const trimmedPath = path.trim();

    const placeholder = placeholderParts.length ? placeholderParts.join(',').trim() : defaultValue;

    const value = get(context, trimmedPath, placeholder);
    return String(value);
  });
export const ImpactAiContextProvider: FC<ContextProviderProps> = ({
  children,
  initialContext = {},
  preset,
  tracker,
}) => {
  // Store the initial context separately for reset purposes
  const storedInitialContext = useRef<ContextStore>(initialContext);

  const baseContext = useMemo(
    () =>
      merge({}, preset?.initialContext || {}, storedInitialContext.current, {
        currentDate: new Date(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    [preset?.initialContext]
  );

  const [context, setContext] = useState<ContextStore>(baseContext);
  const subscribersRef = useRef<Set<(context: ContextStore) => void>>(new Set());

  // Clean up subscriptions on unmount
  useEffect(
    () => () => {
      // Clear all subscriptions when component unmounts
      subscribersRef.current.clear();
    },
    []
  );

  // Helper to notify all subscribers
  const notifySubscribers = useCallback((newContext: ContextStore) => {
    subscribersRef.current.forEach((callback) => {
      try {
        callback(newContext);
      } catch (error) {
        console.error('[ContextAPI] Subscriber callback error:', error);
      }
    });
  }, []);

  const getValue = useCallback(
    <T,>(path: string, defaultValue?: T): T => get(context, path, defaultValue) as T,
    [context]
  );

  const setValue = useCallback(
    (path: string, value: ContextValue): void => {
      setContext((prevContext) => {
        const newContext = cloneDeep(prevContext);
        set(newContext, path, value);

        // Track context update if tracker is available
        if (tracker) {
          tracker.trackContextUpdate(newContext);
        }

        notifySubscribers(newContext); // Notify subscribers
        return newContext;
      });
    },
    [tracker, notifySubscribers]
  );

  const hasValue = useCallback((path: string): boolean => has(context, path), [context]);

  const deleteValue = useCallback(
    (path: string): void => {
      setContext((prevContext) => {
        const newContext = cloneDeep(prevContext);
        unset(newContext, path);

        // Track context update if tracker is available
        if (tracker) {
          tracker.trackContextUpdate(newContext);
        }

        notifySubscribers(newContext); // Notify subscribers
        return newContext;
      });
    },
    [tracker, notifySubscribers]
  );

  const updateValues = useCallback(
    (updates: Record<string, ContextValue>): void => {
      setContext((prevContext) => {
        const newContext = cloneDeep(prevContext);
        Object.entries(updates).forEach(([path, value]) => {
          set(newContext, path, value);
        });

        // Track context update if tracker is available
        if (tracker) {
          tracker.trackContextUpdate(newContext);
        }

        notifySubscribers(newContext); // Notify subscribers
        return newContext;
      });
    },
    [tracker, notifySubscribers]
  );

  const mergeContext = useCallback(
    (newContext: ContextStore): void => {
      setContext((prevContext) => {
        const mergedContext = merge(cloneDeep(prevContext), newContext);

        // Track context update if tracker is available
        if (tracker) {
          tracker.trackContextUpdate(mergedContext);
        }

        notifySubscribers(mergedContext); // Notify subscribers
        return mergedContext;
      });
    },
    [tracker, notifySubscribers]
  );

  const parseTemplate = useCallback(
    (template: string, defaultValue: string | undefined = ''): string =>
      parseTemplateString(template, context, defaultValue),
    [context]
  );

  const resetContext = useCallback(() => {
    // Create a fresh context with only the initial values
    const freshContext = merge({}, preset?.initialContext || {}, storedInitialContext.current, {
      currentDate: new Date(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    setContext(freshContext);

    // Notify subscribers about the reset
    notifySubscribers(freshContext);

    // Track context reset if tracker is available
    if (tracker) {
      tracker.trackContextUpdate(freshContext);
    }
  }, [preset?.initialContext, tracker, notifySubscribers]);

  const getFullContext = useCallback((): ContextStore => cloneDeep(context), [context]);

  const toFlatContext = useCallback((): Record<string, string> => {
    const flattenContext = (obj: ContextStore, prefix = ''): Record<string, string> =>
      Object.entries(obj).reduce((acc, [key, value]) => {
        const newKey = prefix ? `${prefix}.${key}` : key;

        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          return { ...acc, ...flattenContext({ ...value } as ContextStore, newKey) };
        }

        const stringValue = typeof value === 'string' ? value : JSON.stringify(value);

        return { ...acc, [newKey]: stringValue };
      }, {} as Record<string, string>);

    return flattenContext(context);
  }, [context]);

  const subscribe = useCallback((callback: (context: ContextStore) => void) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  const contextApi = useMemo(
    () => ({
      getValue,
      setValue,
      hasValue,
      deleteValue,
      updateValues,
      mergeContext,
      parseTemplate,
      resetContext,
      getFullContext,
      toFlatContext,
      subscribe,
    }),
    [
      getValue,
      setValue,
      hasValue,
      deleteValue,
      updateValues,
      mergeContext,
      parseTemplate,
      resetContext,
      getFullContext,
      toFlatContext,
      subscribe,
    ]
  );

  return <Context.Provider value={contextApi}>{children}</Context.Provider>;
};

export const useImpactAiContext = (): ContextAPI => {
  const context = useContext(Context);

  if (!context) {
    throw new Error('useImpactAiContext must be used within a ContextProvider');
  }

  return context;
};

export const useTemplateParser = () => {
  const { parseTemplate } = useImpactAiContext();
  return parseTemplate;
};
